import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/** Número de suporte padrão da Gravi Águas (somente dígitos, com DDI) */
// Temporário: rede da Gravi Águas fora do ar por causas climáticas
const TELEFONE_PADRAO = '5553984636832';

/**
 * Converte o telefone digitado no cadastro para o formato aceito pelo wa.me
 * (somente dígitos, com DDI). Retorna null quando não há dígitos suficientes.
 */
export function normalizarTelefoneWhatsApp(telefone?: string | null): string | null {
  const digitos = (telefone || '').replace(/\D/g, '');

  // Número local (10 dígitos com fixo, 11 com celular) — assume Brasil
  if (digitos.length === 10 || digitos.length === 11) return `55${digitos}`;

  // Já veio com o DDI 55
  if ((digitos.length === 12 || digitos.length === 13) && digitos.startsWith('55')) return digitos;

  return null;
}

/**
 * Define qual botão de WhatsApp aparece para o usuário logado:
 *
 * - usuário do tipo revenda .................. nenhum botão
 * - sem revenda vinculada .................... botão padrão da Gravi Águas
 * - com revenda vinculada .................... botão da revenda, desde que ela tenha
 *   "mostra botão" ativo e um celular válido; caso contrário, nenhum botão
 *
 * Enquanto os dados da revenda carregam, nada é exibido — assim o botão padrão não
 * aparece por um instante para depois ser trocado ou sumir.
 */
export function useSuporteWhatsApp(): { telefone: string | null; mensagem?: string; avisoTelefones?: boolean } {
  const { user, loading: authLoading } = useAuth();

  const idRevenda = user?.id_revenda;
  const isRevenda = user?.tipo === 'revenda';
  const deveBuscarRevenda = !!idRevenda && !isRevenda;

  const { data: revenda, isPending } = useQuery({
    queryKey: ['revenda-suporte', idRevenda],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('usuarios')
        .select('celular, mostra_botao')
        .eq('id', idRevenda)
        .single();

      if (error) throw error;
      return data as { celular: string | null; mostra_botao: boolean | null };
    },
    enabled: deveBuscarRevenda,
    // O contato da revenda praticamente não muda durante o uso do sistema
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  if (authLoading || !user) return { telefone: null };

  // Revendas não veem botão de suporte
  if (isRevenda) return { telefone: null };

  // Sem revenda vinculada (usuários diretos e administradores): botão padrão
  // A mensagem identifica o cliente, pois o número padrão é provisório e pessoal
  if (!idRevenda) {
    return {
      telefone: TELEFONE_PADRAO,
      // Aviso temporário: mesma regra do botão padrão (sem revenda vinculada)
      avisoTelefones: true,
      mensagem: `Olá! Sou cliente do Sistema Gravi Águas e preciso de suporte técnico. Nome: ${user.nome} | E-mail: ${user.email}`,
    };
  }

  // Vinculado a uma revenda: aguarda a consulta antes de decidir
  if (isPending || !revenda) return { telefone: null };

  if (!revenda.mostra_botao) return { telefone: null };

  return { telefone: normalizarTelefoneWhatsApp(revenda.celular) };
}
